import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { createPatient } from "../api/patients";
import PatientIdBadge from "../components/PatientIdBadge";
import { startTestNow } from "../components/TestLinksSection";

// Physician: регистрация пациента (English production UI).
// Без исследовательских полей (diseases-опросник не нужен в production).

const EDUCATION_OPTIONS = [
  { value: "", label: "Not specified" },
  { value: "incompleteSecondaryEducation", label: "Incomplete secondary" },
  { value: "secondaryEducation", label: "Secondary" },
  { value: "incompleteHigherEducation", label: "Incomplete higher" },
  { value: "higherEducation", label: "Higher" },
  { value: "academicDegree", label: "Academic degree" },
];

const LANGUAGE_OPTIONS = [
  { value: "EN", label: "English" },
  { value: "KK", label: "Kazakh" },
  { value: "RU", label: "Russian" },
];

export default function PhysicianCreatePatientPage() {
  const navigate = useNavigate();
  const [sex, setSex] = useState("");
  const [age, setAge] = useState("");
  const [education, setEducation] = useState("");
  // production-кабинет: язык теста по умолчанию — английский
  const [language, setLanguage] = useState("EN");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [createdId, setCreatedId] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const patient = await createPatient({
        sex,
        age: parseInt(age),
        education: education || undefined,
        language: language || undefined,
        free_notes: notes || undefined,
      });
      setCreatedId(patient.patient_id);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to create patient");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>
        New Patient
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <FormControl fullWidth margin="normal" required>
          <InputLabel>Sex</InputLabel>
          <Select value={sex} label="Sex" onChange={(e) => setSex(e.target.value)}>
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Age"
          type="number"
          value={age}
          onChange={(e) => setAge(e.target.value)}
          margin="normal"
          required
          slotProps={{ htmlInput: { min: 1, max: 120 } }}
        />

        <FormControl fullWidth margin="normal">
          <InputLabel>Education</InputLabel>
          <Select
            value={education}
            label="Education"
            onChange={(e) => setEducation(e.target.value)}
          >
            {EDUCATION_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel>Test language</InputLabel>
          <Select
            value={language}
            label="Test language"
            onChange={(e) => setLanguage(e.target.value)}
          >
            {LANGUAGE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          margin="normal"
          multiline
          rows={3}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading}
          sx={{ mt: 2 }}
        >
          {loading ? "Creating..." : "Create Patient"}
        </Button>
      </form>

      <Dialog open={!!createdId} onClose={() => navigate("/physician")}>
        <DialogTitle>Patient created</DialogTitle>
        <DialogContent sx={{ textAlign: "center", py: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Patient ID:
          </Typography>
          {createdId && <PatientIdBadge patientId={createdId} />}
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 2 }}>
            You can start the test now or send a one-time link from the patient page.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate(`/physician/patient/${createdId}`)}>
            Open patient
          </Button>
          <Button
            variant="contained"
            onClick={() => createdId && startTestNow(createdId, navigate).catch(() => navigate(`/physician/patient/${createdId}`))}
          >
            Start test now
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
